/**
 * Documents — read model for the `/documents` surface.
 *
 * Defines the `DocumentListState` shape (filters + sort + paging + search +
 * view tab + group-by + visible columns), its `defaultState`, and the
 * round-trippable URL-state codec used by `useDocumentsListVM`. URL state is
 * the single source of truth — refreshing or pasting the URL restores the
 * exact view.
 *
 * The toolbar grammar mirrors `/work-items`: Search · + Filter · active chips
 * · spacer · Group by · Properties. Sort uses the Work Items encoding too
 * (`?sort=-last_event_at`) so column-header click-to-sort can swap directions
 * without round-tripping through a dropdown.
 */
import type { Database } from '../../types/database';
import type { SortDescriptor } from '../../components/Table';

/**
 * Authoritative row type — the documents columns plus any extras the RPC
 * tacks on. `body_preview` (first 200 chars of body) is returned by
 * `list_documents` for the Gallery view's card snippet.
 */
export type DocumentRow = Database['public']['Tables']['documents']['Row'] & {
  body_preview?: string | null;
};

/** Page size for `list_documents`. */
export const PAGE_SIZE = 50;

/** View tabs on `/documents`. */
export type DocumentsTab = 'table' | 'gallery';

/** Group-by axes shared by the Table and Gallery views. */
export type DocumentsGroupBy =
  | 'none'
  | 'type'
  | 'lifecycle'
  | 'owner';

/** Stable column key → sortable on the Table view, server-side. */
export type DocumentSortKey =
  | 'last_event_at'
  | 'created_at'
  | 'title';

/** Lifecycle values stored on `documents.lifecycle`. */
export type DocumentLifecycle = 'draft' | 'published' | 'archived';

const LIFECYCLE_VALUES: ReadonlySet<DocumentLifecycle> = new Set([
  'draft',
  'published',
  'archived'
]);

/**
 * Filter facet keys used by the + Filter popover and the chip × buttons.
 * Mirrors the multi-value URL params written by the host page.
 */
export type FilterFacetKey =
  | 'type'
  | 'owner'
  | 'lifecycle'
  | 'created_after'
  | 'created_before'
  | 'updated_after'
  | 'updated_before';

export interface DocumentListState {
  /** `type_id` values. */
  type: ReadonlyArray<string>;
  /** `owner_id` values for the Owner facet typeahead. */
  owner: ReadonlyArray<string>;
  /** `lifecycle` values (draft / published / archived). */
  lifecycle: ReadonlyArray<DocumentLifecycle>;
  /** ISO date string — `created_at >= created_after`. */
  createdAfter: string | null;
  /** ISO date string — `created_at <= created_before`. */
  createdBefore: string | null;
  /** ISO date string — `last_event_at >= updated_after`. */
  updatedAfter: string | null;
  /** ISO date string — `last_event_at <= updated_before`. */
  updatedBefore: string | null;
  /** Full-text + trigram search query (server-side). */
  q: string;
  /** Column + direction. Encoded as `?sort=[-]<column>` (Work Items style). */
  sort: SortDescriptor;
  /** 1-based page index. */
  page: number;
  /** Active view. */
  tab: DocumentsTab;
  /** Group-by axis (Table dividers, Gallery section headers). */
  groupBy: DocumentsGroupBy;
  /**
   * Visible properties — table column keys when `tab === 'table'`, card chip
   * keys when `tab === 'gallery'`. Same `?cols=` URL param round-trips both.
   * An empty array means "use the per-tab defaults".
   */
  cols: ReadonlyArray<string>;
}

export const DEFAULT_SORT: SortDescriptor = {
  columnKey: 'last_event_at',
  direction: 'desc'
};

export const defaultState: DocumentListState = Object.freeze({
  type: [],
  owner: [],
  lifecycle: [],
  createdAfter: null,
  createdBefore: null,
  updatedAfter: null,
  updatedBefore: null,
  q: '',
  sort: DEFAULT_SORT,
  page: 1,
  tab: 'table' as DocumentsTab,
  groupBy: 'none' as DocumentsGroupBy,
  cols: []
});

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  const fromComma = raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (fromComma.length > 1) return fromComma;
  const repeated = params.getAll(key).flatMap((v) =>
    v.split(',').map((s) => s.trim()).filter(Boolean)
  );
  return repeated.length > 0 ? repeated : fromComma;
}

function readLifecycle(params: URLSearchParams): DocumentLifecycle[] {
  return readList(params, 'lifecycle').filter((v): v is DocumentLifecycle =>
    (LIFECYCLE_VALUES as ReadonlySet<string>).has(v)
  );
}

function readDate(params: URLSearchParams, key: string): string | null {
  const v = params.get(key);
  if (!v) return null;
  const trimmed = v.trim();
  return trimmed ? trimmed : null;
}

function readPage(params: URLSearchParams): number {
  const raw = params.get('page');
  if (!raw) return 1;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function readSort(params: URLSearchParams): SortDescriptor {
  const raw = params.get('sort');
  if (!raw) return DEFAULT_SORT;
  const desc = raw.startsWith('-');
  const key = desc ? raw.slice(1) : raw;
  if (!key) return DEFAULT_SORT;
  return { columnKey: key, direction: desc ? 'desc' : 'asc' };
}

function readTab(params: URLSearchParams): DocumentsTab {
  const raw = params.get('tab');
  return raw === 'gallery' ? 'gallery' : 'table';
}

function readGroupBy(params: URLSearchParams): DocumentsGroupBy {
  const raw = params.get('groupBy');
  switch (raw) {
    case 'type':
    case 'lifecycle':
    case 'owner':
      return raw;
    default:
      return 'none';
  }
}

/**
 * Decode a `URLSearchParams` snapshot to a full state. Unknown / invalid
 * values fall back to the corresponding default — the URL is permissive so
 * bad share-links never crash the page.
 */
export function parseSearchParams(params: URLSearchParams): DocumentListState {
  return {
    type: readList(params, 'type'),
    owner: readList(params, 'owner'),
    lifecycle: readLifecycle(params),
    createdAfter: readDate(params, 'created_after'),
    createdBefore: readDate(params, 'created_before'),
    updatedAfter: readDate(params, 'updated_after'),
    updatedBefore: readDate(params, 'updated_before'),
    q: params.get('q')?.trim() ?? '',
    sort: readSort(params),
    page: readPage(params),
    tab: readTab(params),
    groupBy: readGroupBy(params),
    cols: readList(params, 'cols')
  };
}

function encodeSort(sort: SortDescriptor): string {
  return sort.direction === 'desc' ? `-${sort.columnKey}` : sort.columnKey;
}

function sortIsDefault(sort: SortDescriptor): boolean {
  return (
    sort.columnKey === DEFAULT_SORT.columnKey &&
    sort.direction === DEFAULT_SORT.direction
  );
}

/**
 * Encode a state to a `URLSearchParams`. Default-equal values are omitted so
 * the canonical URL for the empty state is `/documents` (no `?`).
 */
export function stateToSearchParams(
  state: DocumentListState
): URLSearchParams {
  const p = new URLSearchParams();
  if (state.type.length > 0) p.set('type', state.type.join(','));
  if (state.owner.length > 0) p.set('owner', state.owner.join(','));
  if (state.lifecycle.length > 0) p.set('lifecycle', state.lifecycle.join(','));
  if (state.createdAfter) p.set('created_after', state.createdAfter);
  if (state.createdBefore) p.set('created_before', state.createdBefore);
  if (state.updatedAfter) p.set('updated_after', state.updatedAfter);
  if (state.updatedBefore) p.set('updated_before', state.updatedBefore);
  if (state.q) p.set('q', state.q);
  if (!sortIsDefault(state.sort)) p.set('sort', encodeSort(state.sort));
  if (state.page > 1) p.set('page', String(state.page));
  if (state.tab !== 'table') p.set('tab', state.tab);
  if (state.groupBy !== 'none') p.set('groupBy', state.groupBy);
  if (state.cols.length > 0) p.set('cols', state.cols.join(','));
  return p;
}

/**
 * True when `state` has any active filter or search — drives the
 * "no filters set" empty-state branch.
 */
export function hasActiveFilters(state: DocumentListState): boolean {
  return (
    state.type.length > 0 ||
    state.owner.length > 0 ||
    state.lifecycle.length > 0 ||
    state.createdAfter !== null ||
    state.createdBefore !== null ||
    state.updatedAfter !== null ||
    state.updatedBefore !== null ||
    state.q.length > 0
  );
}
